import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RefreshCw, Check, X, AlertTriangle, Pause, Copy, ExternalLink } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

// ============ APP PRICE DEFINITIONS ============
// These are the prices defined in the app (from Pricing.tsx and edge functions)

interface AppPrice {
  key: string;
  name: string;
  amount: number; // in EUR
  interval: 'month' | 'year' | 'one_time';
  type: 'subscription' | 'topup';
  testPriceId: string;
  livePriceId: string;
}

const APP_PRICES: AppPrice[] = [
  // Subscriptions (Test Price IDs synced with Live Mode prices 2026-01-27)
  { key: 'starter_monthly', name: 'Starter Monthly', amount: 5, interval: 'month', type: 'subscription', testPriceId: 'price_1SuLFQQZgycKgbhzpUGvwr5r', livePriceId: 'price_1SNdCLQZgycKgbhz4HUdAKlp' },
  { key: 'starter_annual', name: 'Starter Annual', amount: 50, interval: 'year', type: 'subscription', testPriceId: 'price_1SuLFVQZgycKgbhz4qny5s71', livePriceId: 'price_1SNdCsQZgycKgbhzMSjUbFsu' },
  { key: 'pro_monthly', name: 'Pro Monthly', amount: 19, interval: 'month', type: 'subscription', testPriceId: 'price_1SuLFWQZgycKgbhz2U5XZx55', livePriceId: 'price_1SNdD6QZgycKgbhzidzo5ThT' },
  { key: 'pro_annual', name: 'Pro Annual', amount: 190, interval: 'year', type: 'subscription', testPriceId: 'price_1SuLFXQZgycKgbhzTgXy4wke', livePriceId: 'price_1SNdDAQZgycKgbhzqE7FaJdR' },
  { key: 'enterprise_monthly', name: 'Enterprise Monthly', amount: 99, interval: 'month', type: 'subscription', testPriceId: 'price_1SuLFYQZgycKgbhztZKwDqr4', livePriceId: 'price_1SNdDEQZgycKgbhzrND8FKPC' },
  // Top-ups (Test Price IDs synced with Live Mode prices 2026-01-27)
  { key: 'mini', name: 'Mini (100 Credits)', amount: 5, interval: 'one_time', type: 'topup', testPriceId: 'price_1SuLFbQZgycKgbhzekLuFkAM', livePriceId: 'price_1SNdDFQZgycKgbhzlTt6gyO1' },
  { key: 'standard', name: 'Standard (500 Credits)', amount: 18, interval: 'one_time', type: 'topup', testPriceId: 'price_1SuLFcQZgycKgbhzGdZ57Ha5', livePriceId: 'price_1SNdDGQZgycKgbhzDvN0octa' },
  { key: 'power', name: 'Power (2000 Credits)', amount: 55, interval: 'one_time', type: 'topup', testPriceId: 'price_1SuLFdQZgycKgbhziYiz9rCf', livePriceId: 'price_1SNdDHQZgycKgbhzeCk3T6tm' },
  { key: 'agency', name: 'Agency (10000 Credits)', amount: 220, interval: 'one_time', type: 'topup', testPriceId: 'price_1SuLFeQZgycKgbhzFWAhOFKA', livePriceId: 'price_1SNdDIQZgycKgbhzHRLznOyj' },
];

interface StripePrice {
  id: string;
  product_id: string;
  product_name: string;
  unit_amount: number;
  currency: string;
  recurring: { interval: string; interval_count: number } | null;
  active: boolean;
  nickname: string | null;
}

interface StripePricesResponse {
  live_prices: StripePrice[];
  test_prices: StripePrice[];
  has_test_key: boolean;
}

type DiscrepancyStatus = 'match' | 'price_mismatch' | 'missing' | 'inactive' | 'currency_mismatch';

interface PriceComparison {
  appPrice: AppPrice;
  testPrice: StripePrice | null;
  livePrice: StripePrice | null;
  testStatus: DiscrepancyStatus;
  liveStatus: DiscrepancyStatus;
  testDetails?: string;
  liveDetails?: string;
}

export function StripePriceSync() {
  const [loading, setLoading] = useState(false);
  const [stripePrices, setStripePrices] = useState<StripePricesResponse | null>(null);
  const [comparisons, setComparisons] = useState<PriceComparison[]>([]);
  const [filter, setFilter] = useState<'all' | 'subscription' | 'topup'>('all');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const fetchPrices = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('get-stripe-prices');
      
      if (error) throw error;
      
      setStripePrices(data);
      compareAllPrices(data);
      
      toast({
        title: 'Preise aktualisiert',
        description: `${data.live_prices.length} Live, ${data.test_prices.length} Test Preise geladen`,
      });
    } catch (error) {
      console.error('Error fetching prices:', error);
      toast({
        title: 'Fehler beim Laden',
        description: error instanceof Error ? error.message : 'Unbekannter Fehler',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const comparePrice = (
    appPrice: AppPrice,
    stripePrice: StripePrice | undefined
  ): { status: DiscrepancyStatus; details?: string } => {
    if (!stripePrice) {
      return { status: 'missing', details: 'Price ID nicht in Stripe gefunden' };
    }

    if (!stripePrice.active) {
      return { status: 'inactive', details: 'Preis ist in Stripe deaktiviert' };
    }

    if (stripePrice.currency !== 'EUR') {
      return { 
        status: 'currency_mismatch', 
        details: `Erwartet EUR, gefunden ${stripePrice.currency}` 
      };
    }

    const expectedCents = appPrice.amount * 100;
    if (stripePrice.unit_amount !== expectedCents) {
      return { 
        status: 'price_mismatch', 
        details: `App: €${appPrice.amount}, Stripe: €${(stripePrice.unit_amount / 100).toFixed(2)}` 
      };
    }

    // Check interval for subscriptions
    if (appPrice.interval !== 'one_time') {
      if (!stripePrice.recurring) {
        return { status: 'price_mismatch', details: 'Erwartet wiederkehrend, aber einmalig gefunden' };
      }
      if (stripePrice.recurring.interval !== appPrice.interval) {
        return { 
          status: 'price_mismatch', 
          details: `Intervall: erwartet ${appPrice.interval}, gefunden ${stripePrice.recurring.interval}` 
        };
      }
    }

    return { status: 'match' };
  };

  const compareAllPrices = (data: StripePricesResponse) => {
    const results: PriceComparison[] = APP_PRICES.map(appPrice => {
      const testPrice = data.test_prices.find(p => p.id === appPrice.testPriceId);
      const livePrice = data.live_prices.find(p => p.id === appPrice.livePriceId);

      const testResult = comparePrice(appPrice, testPrice);
      const liveResult = comparePrice(appPrice, livePrice);

      return {
        appPrice,
        testPrice: testPrice || null,
        livePrice: livePrice || null,
        testStatus: testResult.status,
        liveStatus: liveResult.status,
        testDetails: testResult.details,
        liveDetails: liveResult.details,
      };
    });

    setComparisons(results);
  };

  useEffect(() => {
    fetchPrices();
  }, []);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Kopiert!',
      description: text,
    });
  };

  const getStatusBadge = (status: DiscrepancyStatus, details?: string) => {
    switch (status) {
      case 'match':
        return (
          <Badge className="bg-primary/10 text-primary border-primary/20">
            <Check className="h-3 w-3 mr-1" /> Match
          </Badge>
        );
      case 'missing':
        return (
          <Badge variant="destructive">
            <X className="h-3 w-3 mr-1" /> Fehlt
          </Badge>
        );
      case 'price_mismatch':
      case 'currency_mismatch':
        return (
          <Badge variant="destructive" title={details}>
            <AlertTriangle className="h-3 w-3 mr-1" /> Abweichung
          </Badge>
        );
      case 'inactive':
        return (
          <Badge className="bg-accent text-accent-foreground border-accent">
            <Pause className="h-3 w-3 mr-1" /> Inaktiv
          </Badge>
        );
    }
  };

  const formatPrice = (price: StripePrice | null, appAmount: number) => {
    if (!price) return '—';
    const stripeAmount = price.unit_amount / 100;
    const isMatch = stripeAmount === appAmount;
    return (
      <span className={isMatch ? 'text-primary' : 'text-destructive'}>
        €{stripeAmount.toFixed(2)}
        {price.recurring && `/${price.recurring.interval === 'month' ? 'mo' : 'yr'}`}
      </span>
    );
  };

  const toggleRow = (key: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedRows(newExpanded);
  };

  const filteredComparisons = comparisons.filter(c => 
    filter === 'all' || c.appPrice.type === filter
  );

  const hasDiscrepancies = comparisons.some(
    c => c.testStatus !== 'match' || c.liveStatus !== 'match'
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              Stripe Price Sync Dashboard
              {hasDiscrepancies && (
                <Badge variant="destructive">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Diskrepanzen gefunden
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              Vergleich der App-Preise mit Stripe Test & Live Mode
            </CardDescription>
          </div>
          <Button onClick={fetchPrices} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs value={filter} onValueChange={(v) => setFilter(v as typeof filter)} className="mb-4">
          <TabsList>
            <TabsTrigger value="all">Alle ({comparisons.length})</TabsTrigger>
            <TabsTrigger value="subscription">
              Subscriptions ({comparisons.filter(c => c.appPrice.type === 'subscription').length})
            </TabsTrigger>
            <TabsTrigger value="topup">
              Top-ups ({comparisons.filter(c => c.appPrice.type === 'topup').length})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {!stripePrices?.has_test_key && (
          <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              ⚠️ STRIPE_TEST_SECRET_KEY ist nicht konfiguriert. Test Mode Preise können nicht abgerufen werden.
            </p>
          </div>
        )}

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[200px]">Plan</TableHead>
                <TableHead className="text-center">App Preis</TableHead>
                <TableHead className="text-center">Test Mode</TableHead>
                <TableHead className="text-center">Live Mode</TableHead>
                <TableHead className="text-center">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredComparisons.map(comparison => (
                <Collapsible key={comparison.appPrice.key} asChild>
                  <>
                    <TableRow 
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => toggleRow(comparison.appPrice.key)}
                    >
                      <TableCell className="font-medium">
                        {comparison.appPrice.name}
                      </TableCell>
                      <TableCell className="text-center">
                        €{comparison.appPrice.amount}
                        {comparison.appPrice.interval !== 'one_time' && 
                          `/${comparison.appPrice.interval === 'month' ? 'mo' : 'yr'}`}
                      </TableCell>
                      <TableCell className="text-center">
                        {formatPrice(comparison.testPrice, comparison.appPrice.amount)}
                      </TableCell>
                      <TableCell className="text-center">
                        {formatPrice(comparison.livePrice, comparison.appPrice.amount)}
                      </TableCell>
                      <TableCell className="text-center space-x-1">
                        {comparison.testStatus === 'match' && comparison.liveStatus === 'match' ? (
                          <Badge className="bg-primary/10 text-primary border-primary/20">
                            <Check className="h-3 w-3 mr-1" /> OK
                          </Badge>
                        ) : (
                          <>
                            {comparison.testStatus !== 'match' && (
                              <span className="text-xs">T: {getStatusBadge(comparison.testStatus)}</span>
                            )}
                            {comparison.liveStatus !== 'match' && (
                              <span className="text-xs">L: {getStatusBadge(comparison.liveStatus)}</span>
                            )}
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                    {expandedRows.has(comparison.appPrice.key) && (
                      <TableRow className="bg-muted/30">
                        <TableCell colSpan={5}>
                          <div className="p-4 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                              {/* Test Mode Details */}
                              <div className="space-y-2">
                                <h4 className="font-semibold text-sm">Test Mode</h4>
                                <div className="text-xs space-y-1">
                                  <div className="flex items-center gap-2">
                                    <span className="text-muted-foreground">Price ID:</span>
                                    <code 
                                      className="bg-muted px-1 rounded cursor-pointer hover:bg-muted/80"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        copyToClipboard(comparison.appPrice.testPriceId);
                                      }}
                                    >
                                      {comparison.appPrice.testPriceId}
                                    </code>
                                    <Copy className="h-3 w-3 text-muted-foreground" />
                                  </div>
                                  {comparison.testPrice && (
                                    <>
                                      <div>
                                        <span className="text-muted-foreground">Produkt:</span> {comparison.testPrice.product_name}
                                      </div>
                                      <div>
                                        <span className="text-muted-foreground">Aktiv:</span> {comparison.testPrice.active ? 'Ja' : 'Nein'}
                                      </div>
                                    </>
                                  )}
                                  {comparison.testDetails && (
                                    <div className="text-destructive">
                                      ⚠️ {comparison.testDetails}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Live Mode Details */}
                              <div className="space-y-2">
                                <h4 className="font-semibold text-sm">Live Mode</h4>
                                <div className="text-xs space-y-1">
                                  <div className="flex items-center gap-2">
                                    <span className="text-muted-foreground">Price ID:</span>
                                    <code 
                                      className="bg-muted px-1 rounded cursor-pointer hover:bg-muted/80"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        copyToClipboard(comparison.appPrice.livePriceId);
                                      }}
                                    >
                                      {comparison.appPrice.livePriceId}
                                    </code>
                                    <Copy className="h-3 w-3 text-muted-foreground" />
                                  </div>
                                  {comparison.livePrice && (
                                    <>
                                      <div>
                                        <span className="text-muted-foreground">Produkt:</span> {comparison.livePrice.product_name}
                                      </div>
                                      <div>
                                        <span className="text-muted-foreground">Aktiv:</span> {comparison.livePrice.active ? 'Ja' : 'Nein'}
                                      </div>
                                    </>
                                  )}
                                  {comparison.liveDetails && (
                                    <div className="text-destructive">
                                      ⚠️ {comparison.liveDetails}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                </Collapsible>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Summary */}
        <div className="mt-4 p-4 bg-muted/50 rounded-lg">
          <h4 className="font-semibold mb-2">Zusammenfassung</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Gesamt:</span>{' '}
              <span className="font-medium">{comparisons.length} Preise</span>
            </div>
            <div>
              <span className="text-primary">✓ OK:</span>{' '}
              <span className="font-medium">
                {comparisons.filter(c => c.testStatus === 'match' && c.liveStatus === 'match').length}
              </span>
            </div>
            <div>
              <span className="text-destructive">✗ Diskrepanzen:</span>{' '}
              <span className="font-medium">
                {comparisons.filter(c => c.testStatus !== 'match' || c.liveStatus !== 'match').length}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Stripe Preise:</span>{' '}
              <span className="font-medium">
                {stripePrices?.live_prices.length || 0} Live, {stripePrices?.test_prices.length || 0} Test
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
